import React, { useRef, useCallback, useEffect, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import {
  Search,
  Users,
  ShieldCheck,
  Clock3,
  MessageSquare,
  MessageCircle,
  Briefcase,
  Star,
  ArrowRight,
  CheckCircle,
  Zap,
  Home,
  Wrench,
  Paintbrush,
  Truck,
  Laptop,
  ChefHat,
  Scissors,
  GraduationCap,
  PartyPopper,
  Building,
  Heart,
  Car,
  Dumbbell,
  Camera,
  Music,
  Shirt,
  Hammer,
  TreePine,
  Package,
  Settings,
} from "lucide-react";

/* ===================== THEME (matches other pages) ===================== */
const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "#059669",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
    md: "0 6px 20px rgba(15, 23, 42, 0.07)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  radius: "14px",
};

/* ===================== GLOBAL & ANIMS ===================== */
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideInUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${theme.colors.background};
    font-family: ${theme.font};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

/* ===================== LAYOUT ===================== */
const Page = styled.div`
  background: ${theme.colors.background};
  min-height: 100vh;
  padding-top: 64px;
  padding-left: 5px;
  padding-right: 5px;
  animation: ${fadeIn} 0.5s ease;
  overflow-x: hidden; // Prevents horizontal scroll from animations
`;

// Updated Hero section for centered content
const Hero = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem 3rem;
  animation: ${slideInUp} 0.6s ease-out;
`;

// Wrapper for the text content in the hero
const HeroTextWrapper = styled.div`
  max-width: 800px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: clamp(2.25rem, 5vw, 3.25rem);
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0;
  letter-spacing: -0.04em;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  max-width: 680px;
  margin: 1.5rem 0 0;
  font-size: 1.125rem;
  color: ${theme.colors.textMuted};
  line-height: 1.6;

  @media (max-width: 992px) {
    margin: 1rem auto 0;
  }
`;

const TrustIndicators = styled.div`
  display: flex;
  gap: 32px;
  margin: 24px 0 32px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 20px;
    justify-content: center;
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #059669;
  font-size: 0.9rem;
  font-weight: 600;
  
  svg {
    color: #059669;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const CategoriesSection = styled.div`
  text-align: center;
  margin: 48px 0;
  
  @media (max-width: 768px) {
    margin: 32px 0;
  }
`;

const CategoriesTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 24px;
`;


const Highlight = styled.span`
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  display: inline-block;
`;

// 🎨 NEW: Wrapper for the SVG Illustration
const HeroIllustrationWrapper = styled.div`
  flex: 0 0 45%;
  max-width: 480px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 100%;
    height: auto;
  }

  // Hide the illustration on smaller screens to save space
  @media (max-width: 992px) {
    display: none;
  }
`;

// 🎨 NEW: SVG Illustration Component
const HeroIllustration = () => (
  <HeroIllustrationWrapper>
    <svg viewBox="0 0 480 320" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="ropeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#fde047" />
          <stop offset="100%" stopColor="#facc15" />
        </linearGradient>
      </defs>
      {/* The large letter 'D' */}
      <path
        d="M140 10H280C346.274 10 400 63.7258 400 130V190C400 256.274 346.274 310 280 310H140V10Z"
        fill="#FFFFFF"
        stroke={theme.colors.border}
        strokeWidth="2"
      />
      <path
        d="M204 74H274C305.941 74 332 100.059 332 132V188C332 219.941 305.941 246 274 246H204V74Z"
        fill={theme.colors.background}
      />
      {/* The ladder */}
      <line
        x1="100"
        y1="310"
        x2="180"
        y2="110"
        stroke={theme.colors.primary}
        strokeWidth="10"
        strokeLinecap="round"
      />
      <line
        x1="120"
        y1="310"
        x2="200"
        y2="110"
        stroke={theme.colors.primary}
        strokeWidth="10"
        strokeLinecap="round"
      />
      <line
        x1="105"
        y1="280"
        x2="125"
        y2="280"
        stroke={theme.colors.primary}
        strokeWidth="8"
        strokeLinecap="round"
      />
      <line
        x1="125"
        y1="230"
        x2="145"
        y2="230"
        stroke={theme.colors.primary}
        strokeWidth="8"
        strokeLinecap="round"
      />
      <line
        x1="145"
        y1="180"
        x2="165"
        y2="180"
        stroke={theme.colors.primary}
        strokeWidth="8"
        strokeLinecap="round"
      />
      <line
        x1="165"
        y1="130"
        x2="185"
        y2="130"
        stroke={theme.colors.primary}
        strokeWidth="8"
        strokeLinecap="round"
      />
      {/* The rope */}
      <path d="M440 0V220" stroke="url(#ropeGradient)" strokeWidth="6" />
      {/* The character */}
      <g transform="translate(410 240)">
        <rect width="60" height="50" rx="10" fill={theme.colors.primary} />
        <circle cx="30" cy="-20" r="20" fill="#fde047" />
        <circle cx="22" cy="-24" r="3" fill="black" />
        <circle cx="38" cy="-24" r="3" fill="black" />
      </g>
    </svg>
  </HeroIllustrationWrapper>
);

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 36px;
  margin-bottom: 80px;
`;

/* ===================== SEARCH ===================== */
const SearchWrapper = styled.form`
  width: 100%;
  max-width: 640px;
  display: flex;
  border-radius: 3rem;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${theme.shadows.sm};

  &:focus-within {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &:hover {
    box-shadow: ${theme.shadows.md};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: ${theme.colors.textHeading};
  min-width: 0;

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 1.75rem;
  border: none;
  border-radius: 0 3rem 3rem 0;
  background: linear-gradient(
    135deg,
    ${theme.colors.primary} 0%,
    #2563eb 100%
  );
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${theme.shadows.sm};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
`;

/* ===================== OTHER SECTIONS (Unchanged) ===================== */
// ... (All other styled components like Section, SectionTitle, CardGrid, etc. remain the same)
const Section = styled.section`
  padding: 2rem 0 0;
  
  @media (max-width: 768px) {
    padding: 1rem 0 0;
  }
`;

const MobileSection = styled(Section)`
  margin-top: 48px;
  
  @media (max-width: 768px) {
    margin-top: 24px;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 1.25rem;
`;

const SectionSub = styled.p`
  text-align: center;
  max-width: 760px;
  margin: 0 auto 2rem;
  color: ${theme.colors.textMuted};
  
  @media (max-width: 768px) {
    margin: 0 auto 1.5rem;
  }
`;

const PopularCategoriesSection = styled(Section)`
  margin-top: 48px;
  
  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-top: 32px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 24px;
  }
`;

const CategoryIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  margin-bottom: 8px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    margin-bottom: 6px;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
`;

const CategoryCard = styled.button`
  background: linear-gradient(135deg, ${theme.colors.surface} 0%, #fafbff 100%);
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(15, 23, 42, 0.1);
    border-color: ${theme.colors.primary};
    background: linear-gradient(135deg, ${theme.colors.surface} 0%, ${theme.colors.primaryLight} 100%);
    
    ${CategoryIcon} {
      background: ${theme.colors.primary};
      color: white;
      
      svg {
        transform: scale(1.1);
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

const CategoryName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 20px;
  }
`;

const FeatureHighlight = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.primaryLight},
    ${theme.colors.surface}
  );
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius};
  padding: 28px;
  box-shadow: ${theme.shadows.sm};
  display: flex;
  align-items: flex-start;
  gap: 18px;
`;

const HighlightIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${theme.radius};
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
`;

const HighlightContent = styled.div`
  flex: 1;
`;

const HighlightTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 1.3rem;
  font-weight: 700;
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  display: inline-block;
`;

const HighlightText = styled.p`
  margin: 0 0 16px;
  color: ${theme.colors.textBody};
  line-height: 1.6;
`;

const HighlightLink = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  border: none;
  color: white;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  padding: 10px 18px;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
  }
`;

const FeatureGrid2 = styled.div`
  display: grid;
  gap: 18px;
`;

const CompactFeature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius};
  padding: 18px;
  box-shadow: ${theme.shadows.sm};
`;

const CompactIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
`;

const CompactTitle = styled.h4`
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
`;

const CompactText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`;

const SuccessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const SuccessCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius};
  padding: 24px;
  box-shadow: ${theme.shadows.sm};
`;

const SuccessQuote = styled.p`
  margin: 0 0 20px;
  font-size: 1rem;
  line-height: 1.6;
  color: ${theme.colors.textBody};
  font-style: italic;
`;

const SuccessAuthor = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SuccessAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${theme.colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SuccessName = styled.div`
  font-weight: 700;
  color: ${theme.colors.textHeading};
  font-size: 0.95rem;
`;


const SuccessStats = styled.div`
  margin-left: auto;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
`;

const CTASection = styled.section`
  margin-top: 48px;
  
  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const CTACard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5fb 100%);
  border-radius: 16px;
  padding: 48px 32px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 32px 24px;
    border-radius: 12px;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
`;

const CTATitle = styled.h2`
  margin: 0 0 16px;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 12px;
  }
`;

const CTAText = styled.p`
  margin: 0 0 32px;
  font-size: 1rem;
  color: ${theme.colors.textMuted};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 24px;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  border: none;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  padding: 14px 28px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
  }

  @media (max-width: 480px) {
    width: 200px;
    justify-content: center;
  }
`;

const SecondaryBtn = styled.button`
  background: white;
  border: 2px solid #e2e8f0;
  color: ${theme.colors.textHeading};
  font-weight: 700;
  font-size: 1rem;
  padding: 12px 28px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #3b82f6;
    color: #3b82f6;
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    width: 200px;
    justify-content: center;
  }
`;

const CTANote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9rem;
  color: ${theme.colors.textMuted};
  font-weight: 600;
`;

/* ===================== FOOTER ===================== */
const Footer = styled.footer`
  background: #f8fafc;
  border-top: 1px solid ${theme.colors.border};
  color: ${theme.colors.textMuted};
  padding: 48px 0 24px;
  
  @media (max-width: 768px) {
    padding: 32px 0 16px;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 48px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    text-align: center;
    margin-bottom: 20px;
  }
`;

const FooterBrand = styled.div`
  h3 {
    color: ${theme.colors.textHeading};
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 12px;
  }

  p {
    line-height: 1.6;
    margin: 0 0 20px;
    max-width: 300px;

    @media (max-width: 768px) {
      margin: 0 auto 20px;
    }
  }
`;

const FooterSection = styled.div`
  h4 {
    color: ${theme.colors.textHeading};
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 16px;
  }
`;

const FooterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 8px;

  a {
    color: ${theme.colors.textMuted};
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s ease;
    cursor: pointer;

    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${theme.colors.border};
  padding-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
    padding-top: 16px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;

  a {
    color: ${theme.colors.textMuted};
    transition: color 0.2s ease;
    cursor: pointer;

    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

/* ===================== HOW IT WORKS SECTION ===================== */
const HowItWorksSection = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5fb 100%);
  padding: 48px 32px;
  margin-top: 48px;
  border-radius: 16px;

  @media (max-width: 768px) {
    padding: 32px 24px;
    margin-top: 32px;
    border-radius: 12px;
  }
`;

const HowItWorksContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const HowItWorksCard = styled.div`
  padding: 40px 32px;
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const HeaderIcon = styled.div`
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  
  @media (max-width: 768px) {
    margin: 0 auto 20px;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 12px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CardSubtitle = styled.p`
  font-size: 1rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const HowItWorksCTA = styled.div`
  text-align: center;
  margin-top: 48px;
  
  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 32px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(59, 130, 246, 0.4);
  }

  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 1rem;
  }
`;

/* ===================== PAGE ===================== */
const HomePage = ({ userState }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [categories, setCategories] = useState([]);

  // Fetch categories for popular categories section
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await request("POST", API_CONSTANT_MAP.fetch_categories);
        if (response?.data?.success) {
          const allCategories = response.data.categories || [];
          
          // Ensure Tutoring & Lessons (category ID 17) is included
          const tutoringCategory = allCategories.find(cat => cat.id === 17);
          let popularCategories = allCategories.slice(0, 8);
          
          // If tutoring category exists but isn't in first 8, replace the last one
          if (tutoringCategory && !popularCategories.find(cat => cat.id === 17)) {
            popularCategories[7] = tutoringCategory;
          }
          
          setCategories(popularCategories);
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Lambda warming effect  
  useEffect(() => {
    const warmLambdas = async () => {
      // Critical endpoints to warm - these are the most commonly used
      const criticalEndpoints = [
        { url: API_CONSTANT_MAP.fetch_tasks, method: "POST", data: {} },
        {
          url: API_CONSTANT_MAP.fetch_task_time_windows,
          method: "POST",
          data: {},
        },
        { url: API_CONSTANT_MAP.signin, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.fetch_task_by_id, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.create_task_offer, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.create_task, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.fetch_posted_tasks, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.list_conversations, method: "POST", data: {} },
      ];

      // Fire warming requests in parallel, ignore errors
      const warmingPromises = criticalEndpoints.map(async (endpoint) => {
        try {
          await request(endpoint.method, endpoint.url, endpoint.data);
        } catch (error) {
          // Silently ignore errors - we just want to warm the Lambdas
          console.debug(`Lambda warming for ${endpoint.url}: OK`);
        }
      });

      // Don't await - let them run in background
      Promise.allSettled(warmingPromises);
    };

    // Warm Lambdas in background without blocking render
    warmLambdas();
  }, []);

  // Check for onboarding redirect (but not if they came from account page)
  useEffect(() => {
    if (userState?.user?.token && userState.user.onboarding_status === 'pending') {
      // Don't redirect if user is trying to access account page
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/account')) {
        navigate('/onboarding');
      }
    }
  }, [userState?.user?.onboarding_status, userState?.user?.token, navigate]);

  const navigateToTasks = useCallback(
    (query = "") => {
      const url = query
        ? `/tasks/?query=${encodeURIComponent(query)}`
        : "/tasks";
      navigate(url);
    },
    [navigate]
  );

  const navigateToCategory = useCallback(
    (categoryId) => {
      navigate(`/tasks/?category=${categoryId}`);
    },
    [navigate]
  );

  const handleSearch = (e) => {
    e.preventDefault();
    navigateToTasks(inputRef.current?.value.trim());
  };

  // Helper function to get category icon
  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    
    // Cleaning & Home
    if (name.includes('clean') || name.includes('house') || name.includes('domestic')) 
      return <Home size={20} />;
    
    // Garden & Landscaping
    if (name.includes('garden') || name.includes('yard') || name.includes('lawn') || name.includes('landscape')) 
      return <TreePine size={20} />;
    
    // Repair & Maintenance
    if (name.includes('repair') || name.includes('fix') || name.includes('maintenance') || name.includes('handyman')) 
      return <Wrench size={20} />;
    
    // Moving & Delivery
    if (name.includes('move') || name.includes('transport') || name.includes('delivery') || name.includes('shipping')) 
      return <Truck size={20} />;
    
    // Painting & Design
    if (name.includes('paint') || name.includes('design') || name.includes('decor') || name.includes('art')) 
      return <Paintbrush size={20} />;
    
    // Pet Services
    if (name.includes('pet') || name.includes('dog') || name.includes('cat') || name.includes('animal')) 
      return <Heart size={20} />;
    
    // Technology & IT
    if (name.includes('tech') || name.includes('computer') || name.includes('digital') || name.includes('web') || name.includes('software')) 
      return <Laptop size={20} />;
    
    // Food & Cooking
    if (name.includes('food') || name.includes('cook') || name.includes('grocery') || name.includes('catering') || name.includes('meal')) 
      return <ChefHat size={20} />;
    
    // Beauty & Personal Care
    if (name.includes('beauty') || name.includes('hair') || name.includes('makeup') || name.includes('nail') || name.includes('spa')) 
      return <Scissors size={20} />;
    
    // Education & Tutoring
    if (name.includes('tutor') || name.includes('lesson') || name.includes('teach') || name.includes('education') || name.includes('training')) 
      return <GraduationCap size={20} />;
    
    // Events & Entertainment
    if (name.includes('event') || name.includes('party') || name.includes('wedding') || name.includes('entertainment')) 
      return <PartyPopper size={20} />;
    
    // Business & Office
    if (name.includes('business') || name.includes('office') || name.includes('admin') || name.includes('accounting')) 
      return <Building size={20} />;
    
    // Automotive
    if (name.includes('car') || name.includes('auto') || name.includes('vehicle') || name.includes('mechanic')) 
      return <Car size={20} />;
    
    // Fitness & Health
    if (name.includes('fitness') || name.includes('gym') || name.includes('workout') || name.includes('health')) 
      return <Dumbbell size={20} />;
    
    // Photography & Media
    if (name.includes('photo') || name.includes('video') || name.includes('media') || name.includes('camera')) 
      return <Camera size={20} />;
    
    // Music & Audio
    if (name.includes('music') || name.includes('audio') || name.includes('sound') || name.includes('instrument')) 
      return <Music size={20} />;
    
    // Fashion & Clothing
    if (name.includes('fashion') || name.includes('clothing') || name.includes('tailor') || name.includes('sewing')) 
      return <Shirt size={20} />;
    
    // Construction & Building
    if (name.includes('construction') || name.includes('building') || name.includes('carpentry') || name.includes('contractor')) 
      return <Hammer size={20} />;
    
    // Assembly & Installation
    if (name.includes('assembly') || name.includes('install') || name.includes('setup') || name.includes('furniture')) 
      return <Package size={20} />;
    
    // Default for any other categories
    return <Settings size={20} />;
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        <NavBar />
        <OuterContainer>
          {/* ✅ UPDATED HERO SECTION */}
          <Hero>
            <HeroTextWrapper>
              <Title>
                Get Any Task <Highlight>Done.</Highlight> <br />
                Big or Small.
              </Title>
              <Subtitle>
                <strong>Post any task</strong> and get multiple proposals from qualified people. 
                Choose the best offer based on price, timeline, and reviews.
              </Subtitle>
            </HeroTextWrapper>
            {/* <HeroIllustration /> */}
          </Hero>

          <ContentWrapper>
            <SearchWrapper onSubmit={handleSearch}>
              <SearchInput
                ref={inputRef}
                type="search"
                placeholder="Try 'house painting', 'furniture assembly', or 'grocery shopping'"
                autoComplete="off"
              />
              <SearchButton type="submit" aria-label="Search">
                <Search size={20} />
              </SearchButton>
            </SearchWrapper>
          </ContentWrapper>

          {/* Popular Categories */}
          {categories.length > 0 && (
            <PopularCategoriesSection>
              <SectionTitle>Popular Categories</SectionTitle>
              <SectionSub>
                Browse tasks by category and find what you're looking for
              </SectionSub>
              <CategoriesGrid>
                {categories.map((category) => (
                  <CategoryCard
                    key={category.id}
                    onClick={() => navigateToCategory(category.id)}
                  >
                    <CategoryIcon>
                      {getCategoryIcon(category.name)}
                    </CategoryIcon>
                    <CategoryName>{category.name}</CategoryName>
                  </CategoryCard>
                ))}
              </CategoriesGrid>
            </PopularCategoriesSection>
          )}

          {/* Features */}
          <MobileSection>
            <SectionTitle>How it works</SectionTitle>
            <SectionSub>
              Get your task done in 3 simple steps
            </SectionSub>
            <HowItWorksSection>
              <HowItWorksContainer>
                <HowItWorksCard>
                  <HeaderIcon>
                    <Users size={26} />
                  </HeaderIcon>
                  <CardTitle>Post your task</CardTitle>
                  <CardSubtitle>Tell us what you need done and when. It's free to post and takes just minutes!</CardSubtitle>
                </HowItWorksCard>

                <HowItWorksCard>
                  <HeaderIcon>
                    <MessageCircle size={26} />
                  </HeaderIcon>
                  <CardTitle>Get offers</CardTitle>
                  <CardSubtitle>Receive competitive offers from skilled people in your area within hours.</CardSubtitle>
                </HowItWorksCard>

                <HowItWorksCard>
                  <HeaderIcon>
                    <CheckCircle size={26} />
                  </HeaderIcon>
                  <CardTitle>Get it done</CardTitle>
                  <CardSubtitle>Choose your favorite offer and get your task completed safely and efficiently.</CardSubtitle>
                </HowItWorksCard>
              </HowItWorksContainer>
              
              <HowItWorksCTA>
                <CTAButton
                  onClick={() => {
                    if (!userState?.user?.user_id) {
                      navigate("/signin");
                      return;
                    }
                    navigate("/posttask");
                  }}
                >
                  <Users size={20} />
                  Post your first task
                </CTAButton>
              </HowItWorksCTA>
            </HowItWorksSection>
          </MobileSection>

          {/* Success Stories */}
          <MobileSection>
            <SectionTitle>What our customers are saying</SectionTitle>
            <SectionSub>Real experiences from people getting things done</SectionSub>
            <SuccessGrid>
              <SuccessCard>
                <SuccessAuthor>
                  <SuccessName>Elizabeth P.</SuccessName>
                </SuccessAuthor>
                <SuccessQuote>
                  "Maria assembled my IKEA bedroom set perfectly in just under 2 hours. She brought all her own tools and even helped me organize the leftover hardware. The dresser drawers slide so smoothly now!"
                </SuccessQuote>
              </SuccessCard>
              <SuccessCard>
                <SuccessAuthor>
                  <SuccessName>Tiffany B.</SuccessName>
                </SuccessAuthor>
                <SuccessQuote>
                  "James did an incredible job installing my new ceiling fan and fixing the electrical outlet that wasn't working. He was professional, cleaned up completely, and explained everything he did. Highly recommend!"
                </SuccessQuote>
              </SuccessCard>
              <SuccessCard>
                <SuccessAuthor>
                  <SuccessName>Amanda L.</SuccessName>
                </SuccessAuthor>
                <SuccessQuote>
                  "I needed my bathroom tile resealed and Roberto came with all the right materials. He was punctual, worked efficiently, and the bathroom looks amazing. The price was exactly what we agreed on."
                </SuccessQuote>
              </SuccessCard>
              <SuccessCard>
                <SuccessAuthor>
                  <SuccessName>Sabrina K.</SuccessName>
                </SuccessAuthor>
                <SuccessQuote>
                  "Lisa helped me organize my entire garage in one weekend. She sorted everything systematically and even labeled the storage bins. I can actually find things now! Worth every penny."
                </SuccessQuote>
              </SuccessCard>
            </SuccessGrid>
          </MobileSection>

          {/* CTA */}
          <CTASection>
            <CTACard>
              <CTAContent>
                <CTATitle>Ready to post your first task?</CTATitle>
                <CTAText>
                  Join thousands of people who get their tasks done quickly through competitive proposals
                </CTAText>
                <CTAButtons>
                  <PrimaryBtn
                    onClick={() => {
                      if (!userState?.user?.user_id) {
                        navigate("/signin");
                        return;
                      }
                      navigate("/posttask");
                    }}
                  >
                    <Users size={18} />
                    Post a Task
                  </PrimaryBtn>
                  <SecondaryBtn onClick={() => navigate("/tasks")}>
                    <Briefcase size={18} />
                    Browse Tasks
                  </SecondaryBtn>
                </CTAButtons>
                <CTANote>
                  <CheckCircle size={16} />
                  Free to post • No upfront fees • Get proposals in hours
                </CTANote>
              </CTAContent>
            </CTACard>
          </CTASection>
        </OuterContainer>

        {/* Footer */}
        <Footer>
          <OuterContainer>
            <FooterContent>
              <FooterBrand>
                <h3>Doitly</h3>
                <p>
                  The marketplace connecting task posters with people ready to
                  help. Get things done or earn money with your skills.
                </p>
              </FooterBrand>

              <FooterSection>
                <h4>For Task Posters</h4>
                <FooterList>
                  <FooterLink>
                    <a onClick={() => navigate("/posttask")}>Post a Task</a>
                  </FooterLink>
                  <FooterLink>
                    <a onClick={() => navigate("/tasks")}>Browse Proposals</a>
                  </FooterLink>
                  <FooterLink>
                    <a onClick={() => navigate("/howitworks")}>How It Works</a>
                  </FooterLink>
                  <FooterLink>
                    <a onClick={() => navigate("/safety")}>Safety Tips</a>
                  </FooterLink>
                </FooterList>
              </FooterSection>

              <FooterSection>
                <h4>For Task Helpers</h4>
                <FooterList>
                  <FooterLink>
                    <a onClick={() => navigate("/tasks")}>Browse Tasks</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Submit Proposals</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Success Tips</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Profile Guide</a>
                  </FooterLink>
                </FooterList>
              </FooterSection>

              <FooterSection>
                <h4>Support</h4>
                <FooterList>
                  <FooterLink>
                    <a>Help Center</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Contact Us</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Community Guidelines</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Trust & Safety</a>
                  </FooterLink>
                </FooterList>
              </FooterSection>
            </FooterContent>

            <FooterBottom>
              <div>
                © 2024 Doitly Inc. All rights reserved. | Privacy Policy | Terms
                of Service
              </div>
              <SocialLinks>
                <a>Twitter</a>
                <a>LinkedIn</a>
                <a>Facebook</a>
              </SocialLinks>
            </FooterBottom>
          </OuterContainer>
        </Footer>
      </Page>
    </>
  );
};

const mapStateToProps = (state) => ({ userState: state });
export default connect(mapStateToProps)(HomePage);
